import { PlusIcon, UserPlusIcon, TrashIcon } from "@heroicons/react/24/outline"
import { OrganizationWithTeam } from "@sequel-care/types"
import { deleteOrganization, getAllOrganizations } from "api"
import { Toggle } from "components/common"
import CloudImg from "components/common/CloudImg"
import { OrganizationAddModal } from "components/OrganizationAdd"
import ItemList, { AvatarField } from "components/Settings/ItemList"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "store"
import { useCurrentRole } from "store/hooks"
import { openUserModal } from "store/modal/actions"
import SettingsSection from "./SettingsSection"
import { roleField } from "./utils"
import { TextInputV2, TypographyV2, ButtonV2 } from "components/v2/components/common"
import { classNames } from "utils"
import Modal from "components/common/Modal"
import { CloseIcon } from "components/icons"
import { toast } from "react-toastify"
import { Loader } from "@aws-amplify/ui-react"

const OrganizationList = () => {
    const [loading, setLoading] = useState(true)
    const [organizations, setOrganizations] = useState<OrganizationWithTeam[]>([])
    const [showModal, setShowModal] = useState(false)
    const currentRole = useCurrentRole()

    const actions = [
        {
            Icon: <PlusIcon className="w-4" />,
            translationKey: "newOrganization",
            onClick: () => setShowModal(true)
        }
    ]

    useEffect(() => {
        ;(async () => {
            try {
                setOrganizations(await getAllOrganizations(true))
            } catch (error) {
                console.error(error)
                // Nothing to do here
            } finally {
                setLoading(false)
            }
        })()
    }, [currentRole])

    const deleteOrganizationFromList = (id: number) => {
        const filteredOrganizations = [...organizations].filter((org) => org.id != id)
        setOrganizations(filteredOrganizations)
    }

    const isSuperAdmin = currentRole.role === "admin" && currentRole.organization_id == 3
    return (
        <SettingsSection title="allOrganizations" loading={loading} actions={actions}>
            <div className="mb-4 divide-y divide-gray-200 rounded w-150">
                {organizations.map((organization) => (
                    <RenderOrganization
                        key={organization.id}
                        setOrganization={deleteOrganizationFromList}
                        isSuperAdmin={isSuperAdmin}
                        {...organization}
                    />
                ))}
            </div>
            <OrganizationAddModal show={showModal} setShow={setShowModal} />
        </SettingsSection>
    )
}

const RenderOrganization = ({
    id,
    logo_url,
    name,
    users,
    setOrganization,
    isSuperAdmin
}: OrganizationWithTeam & { setOrganization: (orgId: number) => void; isSuperAdmin: boolean }) => {
    const dispatch = useDispatch()
    const { t } = useTranslation("common")
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [deleteConfirmation, setDeleteConfirmation] = useState("")
    const [isDeleting, setIsDeleting] = useState(false)

    const handleDelete = async () => {
        try {
            setIsDeleting(true)
            await deleteOrganization(id)
            setOrganization(id)
        } catch (error) {
            toast.error(t("common:deleteOrganizationError"))
            console.error(error)
        }
        setIsDeleteModalOpen(false)
        setDeleteConfirmation("")
        setIsDeleting(false)
        toast.success(t("common:deleteOrganizationSuccess"))
    }

    return (
        <>
            <Toggle>
                <div>
                    <div className="flex items-stretch py-4 gap-2 group">
                        <Toggle.Chevron />
                        <div>{logo_url && <CloudImg variant={"large"} width="20" height="20" src={logo_url} />}</div>
                        <div>{name}</div>
                        <div className="ltr:ml-auto rtl:mr-auto flex items-center gap-2">
                            <div>
                                <UserPlusIcon
                                    onClick={() => dispatch(openUserModal({ organization_id: id }))}
                                    className="w-5 cursor-pointer hover:scale-110 text-gray-400 hover:text-dark-blue transition"
                                />
                            </div>
                            {Number(process.env.NEXT_PUBLIC_SEQUEL_ORG_ID) !== id && isSuperAdmin && (
                                <div>
                                    <TrashIcon
                                        className="w-5 cursor-pointer hover:scale-110 text-gray-400 hover:text-dark-blue transition"
                                        onClick={() => setIsDeleteModalOpen(true)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <Toggle.Content
                        className="pb-5 px-8"
                        transitionDuration={users.length <= 6 ? 300 : users.length * 30}
                    >
                        {users.length ? (
                            <div className="grid" style={{ gridTemplateColumns: "2fr 1fr 0fr" }}>
                                <ItemList
                                    items={users}
                                    fields={[
                                        {
                                            key: "user",
                                            RenderField: AvatarField
                                        },
                                        roleField
                                    ]}
                                />
                            </div>
                        ) : (
                            <div className="text-gray-400 italic">{t("noUsers")}</div>
                        )}
                    </Toggle.Content>
                </div>
            </Toggle>

            <Modal
                show={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                centered
                cardClassName="w-2/4  max-w-screen-lg"
            >
                {isDeleting ? (
                    <div className="flex justify-center items-center h-full">
                        <Loader />
                    </div>
                ) : (
                    <div className="flex flex-col w-full gap-4">
                        <div className="p-4 flex justify-between border-b border-border-blue">
                            <TypographyV2 width="bold" type="base" className="text-med-black">
                                {t("common:deleteOrganization")}
                            </TypographyV2>
                            <div className="flex items-center">
                                <CloseIcon
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    className="cursor-pointer text-[#9CA3AF]"
                                    height={20}
                                    width={20}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col w-full p-4">
                            <TypographyV2 type="lg" className="mb-2">
                                {t("deleteOrganizationConfirmation")}
                            </TypographyV2>
                            <TypographyV2 type="base" className="text-gray-500 mb-4">
                                {t("deleteOrganizationConfirmationInputType")}{" "}
                                <span className="font-[900] italic text-red-500">{`DELETE ${name}`}</span>{" "}
                                {t("deleteOrganizationConfirmationInputConfirm")}
                            </TypographyV2>
                            <TextInputV2
                                value={deleteConfirmation}
                                onChange={(e) => setDeleteConfirmation(e)}
                                placeholder={`DELETE ${name}`}
                            />
                        </div>
                        <div className={classNames("flex p-4 border-t border-border-blue justify-end")}>
                            <div className="flex gap-2.5 justify-end">
                                <ButtonV2
                                    variant="light-gray"
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    text={t("common:cancel")}
                                />
                                <ButtonV2
                                    variant="danger"
                                    isDisabled={deleteConfirmation !== `DELETE ${name}`}
                                    onClick={handleDelete}
                                    text={t("common:delete")}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    )
}

export default OrganizationList
